/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Divider, Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import SectionHeadingComponent from 'bundles/browse-common/components/SectionHeadingComponent';

export type AdditionalContent = {
  text: string;
  icon: React.ReactElement;
};

type Props = {
  title: string;
  description?: string | null;
  additionalContent?: AdditionalContent[] | null;
  showPageTitleHeadingSize?: boolean;
};

const styles = {
  icon: css`
    margin-bottom: -2px;
  `,
  additionalContent: css`
    ${breakpoints.up('md')} {
      gap: var(--cds-spacing-300);
    }
  `,
  smallScreenItem: css`
    margin-right: var(--cds-spacing-50);
  `,
  smallScreenAdditionalContent: css`
    margin-top: var(--cds-spacing-200);
  `,
};

const ValuePropositionComponent = (props: Props) => {
  const { title, description, additionalContent, showPageTitleHeadingSize = false } = props;
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  const hasAdditionalContent = additionalContent && additionalContent.length > 0;

  return (
    <Grid container spacing={12}>
      <Grid item xs={12} md={hasAdditionalContent ? 6 : 12}>
        <SectionHeadingComponent showPageTitleHeadingSize={showPageTitleHeadingSize}>{title}</SectionHeadingComponent>
        {description && (
          <Typography2 component="span" variant="bodyPrimary" color="supportText">
            {description}
          </Typography2>
        )}
      </Grid>
      {hasAdditionalContent && (
        <Grid
          container
          item
          xs={12}
          md={6}
          css={[styles.additionalContent, isSmallScreen && styles.smallScreenAdditionalContent]}
          alignContent="center"
          justifyContent={isSmallScreen ? 'flex-start' : 'center'}
          direction="row"
        >
          {additionalContent.map((content, index) => {
            const hasMoreItems = index < additionalContent.length - 1;
            return isSmallScreen ? (
              <Grid item key={content.text} css={styles.smallScreenItem}>
                <Typography2 component="span" variant="bodyPrimary" color="supportText">
                  {content.text}
                  {hasMoreItems && ','}
                </Typography2>
              </Grid>
            ) : (
              <React.Fragment key={content.text}>
                <Grid item>
                  <div css={styles.icon}>{content.icon}</div>
                  <Typography2 component="span" variant="bodyPrimary" color="supportText">
                    {content.text}
                  </Typography2>
                </Grid>
                {hasMoreItems && <Divider color="light" orientation="vertical" />}
              </React.Fragment>
            );
          })}
        </Grid>
      )}
    </Grid>
  );
};

export default ValuePropositionComponent;
